import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Dumbbell, Apple, BrainCircuit, Check } from 'lucide-react';
import { programsContent } from '../data';
import ScrollReveal from '../components/ScrollReveal';
import SEO from '../components/SEO';

export default function ForloebPriser() {
  const { intro, programs } = programsContent;

  return (
    <div className="bg-slate-50/50 pt-16 sm:pt-24">
      <SEO 
        title="Coaching Forløb & Priser | Mindset & Sundhed | SECoaching"
        description="Se vores coachingforløb og priser. Vi tilbyder personlig mindsetcoaching og kommende forløb inden for kostvejledning og træning. Tag næste skridt her!"
      />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24">
        {/* Header */}
        <ScrollReveal direction="up" duration={800} className="text-center max-w-3xl mx-auto space-y-4 mb-16">
          <h1 className="text-4xl font-extrabold tracking-tight text-blue-950 sm:text-5xl">
            Forløb & Priser
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            {intro}
          </p>
        </ScrollReveal>

        {/* Programs Grid */}
        <ScrollReveal direction="up" duration={800} className="grid grid-cols-1 gap-8 lg:grid-cols-3 items-stretch">
          {programs.map((program, idx) => {
            const isActive = program.active;
            return (
              <div
                key={program.id}
                className={`relative flex flex-col rounded-none bg-white p-8 shadow-xl transition-all duration-300 ${
                  isActive
                    ? 'scale-102 z-10 shadow-blue-900/5'
                    : 'opacity-90 hover:opacity-100'
                }`}
              >
                {/* Icon (Black, no box) */}
                <div className="mb-6 flex items-center justify-start">
                  {program.id === 'mindsetforloeb' && <BrainCircuit className="h-8 w-8 text-slate-900" />}
                  {program.id === 'kostvejledning' && <Apple className="h-8 w-8 text-slate-900" />}
                  {program.id === 'personlig-traening' && <Dumbbell className="h-8 w-8 text-slate-900" />}
                </div>

                {/* Content */}
                <div className="flex-1 space-y-4">
                  <h3 className="text-2xl font-bold text-blue-950">{program.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{program.description}</p>

                  {/* Bullet points for styling */}
                  {isActive ? (
                    <ul className="space-y-2.5 text-sm text-slate-600 pt-2">
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-slate-900 shrink-0" />
                        <span>Personlig 1:1 coaching</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-slate-900 shrink-0" />
                        <span>Fokus på vaner, tanker & mønstre</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-slate-900 shrink-0" />
                        <span>Konkrete handlingsplaner</span>
                      </li>
                    </ul>
                  ) : (
                    <ul className="space-y-2.5 text-sm text-slate-400 pt-2">
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-slate-900 shrink-0" />
                        <span>Helhedsorienteret tilgang</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-slate-900 shrink-0" />
                        <span>Fysisk og mental synergi</span>
                      </li>
                    </ul>
                  )}
                </div>

                {/* Price & CTA */}
                <div className="mt-8 pt-6 border-t border-slate-100 space-y-4">
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm font-medium text-slate-400">Pris</span>
                    <span
                      className={`text-lg font-bold ${
                        isActive ? 'text-blue-950' : 'text-slate-500'
                      }`}
                    >
                      {program.price}
                    </span>
                  </div>

                  {isActive ? (
                    <Link
                      to={program.link}
                      className="flex w-full items-center justify-center gap-2 rounded-none bg-blue-950 py-3 text-center text-sm font-semibold text-white hover:bg-blue-900 hover:-translate-y-1 transition-all duration-300"
                    >
                      {program.cta}
                      <ArrowRight className="h-4 w-4 text-sky-400" />
                    </Link>
                  ) : (
                    <Link
                      to="/kontakt"
                      className="flex w-full items-center justify-center gap-2 rounded-none border border-slate-200 bg-white py-3 text-center text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:-translate-y-1 transition-all duration-300"
                    >
                      {program.cta}
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </ScrollReveal>
      </div>

      {/* Trust banner - Full Width */}
      <div className="bg-blue-950 text-white py-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 bg-[radial-gradient(circle_at_top_right,rgba(56,189,248,0.4),transparent_50%)]" />
        <ScrollReveal direction="up" duration={800} className="mx-auto max-w-7xl relative z-10 grid grid-cols-1 gap-8 md:grid-cols-12 items-center">
          <div className="md:col-span-8 space-y-3">
            <h3 className="text-xl sm:text-2xl font-bold">Er du i tvivl om, hvilket forløb der passer til dig?</h3>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Lad os tage en uforpligtende snak om din situation, dine mål og dine udfordringer. Det koster ingenting at spørge.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 rounded-none bg-sky-400 px-6 py-3 text-sm font-bold text-blue-950 hover:bg-sky-300 hover:-translate-y-1 transition-all duration-300"
            >
              <Calendar className="h-4 w-4" />
              Book en samtale
            </Link>
          </div>
        </ScrollReveal>
      </div>
    </div>
  );
}
